import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---- Silent token refresh on 401 ----
// The backend sets an httpOnly `sb-access-token` cookie that expires after 1h.
// When a request returns 401, transparently POST /auth/refresh (which uses the
// 30-day `sb-refresh-token` cookie) and retry the original request ONCE. If the
// refresh itself 401s, bounce to /login.
let refreshPromise = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = (original?.url || "").replace(API, "");

    // Don't retry: the refresh endpoint itself, auth endpoints, or already-retried requests.
    const isAuthEndpoint = url.startsWith("/auth/");
    if (status !== 401 || original?._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      // Dedupe concurrent refreshes across parallel failing requests.
      refreshPromise = refreshPromise || api.post("/auth/refresh").finally(() => {
        refreshPromise = null;
      });
      await refreshPromise;
      return api(original);
    } catch (refreshErr) {
      // Refresh itself failed — session dead. Bounce to /login unless already there.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        const back = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/login?redirect=${back}`);
      }
      return Promise.reject(refreshErr);
    }
  }
);

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
